// This should probably be setup as a hook in react if going to be leveraged in a more custom checkout flow
// but it's done this way since the majority of state management is handled in the parent payment providers pages
// we simply respond to events when they trigger, and resolve or reject them as necessary
// and pass back any errors messages
import { isNotNullish } from '@/lib/utils';
import { ClickResolveDetails, ExpressCheckoutAddress, ShippingRate } from '@stripe/stripe-js';
import {
  AddressInput,
  CheckoutFragment,
  Currency,
  DeliveryOptionsFragment,
  GetDeliveryOptionsInput,
  UpdateCheckoutInput,
} from '@/gql';
import { getCheckout, updateCheckout } from '@/api';
import { isCheckoutErrorFragment, isCheckoutFragment } from '@/lib/gql';
import { UpdateCheckoutAddressInput } from '@/types/checkout';

import { DELIVERY_OPTION_TO_CHECKOUT_DELIVERY_OPTION } from '@/constants/checkout';

/**
 * Returns the options for express checkout. These options include requirements for email, phone number, and shipping address,
 * as well as initial line items (pre shipping/tax data) and allowed shipping countries.
 *
 * Initial line items and pricing data isn't trusted here as well. It is simply for the original render
 * which then will be updated with values received from our checkout-service when we get the shipping address.
 *
 * @param {number} subTotal - The subtotal amount for the checkout.
 * @param {ShippingRate[]} shippingResults - The shipping rates for the checkout.
 * @returns {ClickResolveDetails} - The options for express checkout.
 */
export const getExpressCheckoutOptions = (subTotal: number, shippingResults?: ShippingRate[]): ClickResolveDetails => ({
  // We don't know tax or shipping costs yet - so settings them to 0 until we get buyers shipping address
  lineItems: [
    {
      name: 'Subtotal',
      amount: subTotal,
    },
    {
      name: 'Tax (not calculated)',
      amount: 0,
    },
    {
      name: 'Shipping (not calculated)',
      amount: 0,
    },
  ],
  // TODO: Figure out if we want some kind of default - right now it isn't important as it's updated once we get pricing
  // but it's required for the initial render
  // summary info from the checkout-service when sending the address.
  shippingRates: shippingResults || [],
});

/**
 * Converts an ExpressCheckoutPartialAddress to an AddressInput (for interfacing with Stripe).
 *
 * @param {ExpressCheckoutAddress} Partial - The address to be converted.
 * @returns {AddressInput} - The converted address.
 */
export const getAddressAsAddressInput = (address: Partial<ExpressCheckoutAddress>): AddressInput => ({
  address1: address.line1 || '',
  address2: address.line2 || '',
  city: address.city,
  state: address.state,
  stateCode: `${address.country}-${address.state}`,
  zip: address.postal_code,
  country: address.country,
  countryCode: 'USA', // TODO: implement others - just US for now
});

/**
 * Returns the payload for updating the shipping address of a checkout.
 *
 * @param {ExpressCheckoutAddress} address - The new shipping address.
 * @param {string} storeSlug - The slug of the store.
 * @param {string} checkoutId - The ID of the checkout.
 * @returns {UpdateCheckoutAddressInput} - The payload for updating the shipping address.
 */
export const stripeAddressToAddressInputPayload = (
  address: ExpressCheckoutAddress | Partial<ExpressCheckoutAddress>,
  storeSlug: string,
  checkoutId: string = '' // Used without ID for getting delivery options
): UpdateCheckoutAddressInput => ({
  data: {
    storeSlug,
    deliveryOption: DELIVERY_OPTION_TO_CHECKOUT_DELIVERY_OPTION.standard,
    customer: {
      address: getAddressAsAddressInput(address),
    },
  },
  id: checkoutId,
});

/**
 * Returns the costs from a checkout record if they are valid. The costs include line items and total cost.
 * Throws an error if the costs are missing.
 *
 * @param {CheckoutFragment} checkout - The checkout record.
 * @returns {Object} - The costs from the checkout record.
 * @throws {Error} - If the costs are missing or invalid.
 */
export const getValidCostsFromCheckoutRecord = (checkout: CheckoutFragment) => {
  if (
    isNotNullish(checkout.costs) &&
    isNotNullish(checkout.costs?.total) &&
    isNotNullish(checkout.costs?.shipping) &&
    isNotNullish(checkout.costs?.tax) &&
    isNotNullish(checkout.costs?.total?.value) &&
    isNotNullish(checkout.costs?.shipping?.value) &&
    isNotNullish(checkout.costs?.tax?.value)
  ) {
    const stripeLineItems = [
      {
        name: 'Subtotal',
        amount: checkout.costs.total.value - checkout.costs.shipping.value - checkout.costs.tax.value,
      },
      { name: 'Tax', amount: checkout.costs.tax.value },
      { name: 'Shipping', amount: checkout.costs.shipping.value },
    ];
    // Account for already applied promo code - note the negative value here used for display
    if (isNotNullish(checkout.costs?.discount)) {
      stripeLineItems.push({ name: 'Discount', amount: -Math.abs(checkout.costs?.discount?.value || 0) });
    }
    return { lineItems: stripeLineItems, total: checkout.costs.total.value, currency: checkout.costs.total.currency };
  } else {
    throw new Error('Costs are missing or invalid from the checkout record');
  }
};

const VALIDATE_CHECKOUT_ERROR_MESSAGE = (checkout: Record<string, any>) =>
  `Invalid checkout record received: ${JSON.stringify(checkout)}`;
/**
 * Validates a checkout record. If the checkout record is not a CheckoutFragment, it throws a CheckoutRecordError.
 * If the checkout record is a CheckoutErrorFragment, it throws a CheckoutRecordError with the data from the CheckoutErrorFragment.
 *
 * @param {any} checkout - The checkout record to be validated.
 * @returns {boolean} - True if the checkout record is a CheckoutFragment.
 * @throws {CheckoutRecordError} - If the checkout record is not a CheckoutFragment or is a CheckoutErrorFragment.
 */
export const isValidCheckoutFragment = (checkout: any): checkout is CheckoutFragment => {
  if (!isCheckoutFragment(checkout)) {
    if (isCheckoutErrorFragment(checkout)) {
      throw new Error(checkout?.message || VALIDATE_CHECKOUT_ERROR_MESSAGE(checkout));
    }
    throw new Error(checkout?.message || VALIDATE_CHECKOUT_ERROR_MESSAGE(checkout));
  }
  return true;
};

/**
 * Fetches and validates a checkout record. If the checkout record is valid, it is returned.
 * If the checkout record is not valid, a CheckoutRecordError is thrown.
 *
 * @param {string} checkoutId - The ID of the checkout.
 * @param {string} storeSlug - The slug of the store.
 * @returns {Promise<CheckoutFragment>} - The valid checkout record.
 * @throws {ExpressCheckoutError} - If the checkout record is not valid.
 */
export const getCheckoutRecord = async (checkoutId: string, storeSlug: string): Promise<CheckoutFragment> => {
  try {
    const checkout = await getCheckout(checkoutId, storeSlug);
    if (isValidCheckoutFragment(checkout)) {
      return checkout;
    } else {
      throw new Error('');
    }
  } catch {
    throw new Error('An error occurred while fetching or validating your order. Please reload the page and try again.');
  }
};

/**
 * Updates and validates a checkout record. If the checkout record is valid after the update, it is returned.
 * If the checkout record is not valid after the update, a CheckoutRecordError is thrown.
 *
 * @param {UpdateCheckoutInput | UpdateCheckoutAddressInput} payload - The payload for updating the checkout record.
 * @returns {Promise<CheckoutFragment>} - The valid checkout record after the update.
 * @throws {CheckoutRecordError} - If the checkout record is not valid after the update.
 */
export const updateCheckoutRecord = async (
  payload: UpdateCheckoutInput | UpdateCheckoutAddressInput
): Promise<CheckoutFragment> => {
  let checkout;
  try {
    // UpdateCheckoutAddressInput is a valid UpdateCheckoutInput - just has values actually set instead of optional
    checkout = await updateCheckout(payload as UpdateCheckoutInput);
    isValidCheckoutFragment(checkout);
  } catch {
    throw new Error('Unable to update the checkout with changes. Please try again.');
  }
  return checkout as CheckoutFragment;
};
/**
 * Returns the costs from a checkout record if they are valid. The costs include line items and total cost.
 * @param quote
 * @returns {boolean}
 */
export const deliveryOptionHasNameAndPrice = (quote: any): quote is ShippingRate => {
  return isNotNullish(quote?.name) && isNotNullish(quote?.cost?.amount);
};

/**
 * Returns the costs from a checkout record if they are valid. The costs include line items and total cost.
 * @param deliveryOptions
 * @returns {boolean}
 */
export const deliveryOptionsHaveQuotes = (deliveryOptions: DeliveryOptionsFragment) => {
  return deliveryOptions?.attributes?.quotes?.length > 0 || false;
};

/**
 * Returns the payload for getting delivery options. The payload includes currency, country code, line items, checkout ID, state code, and store slug.
 *
 * @param {CheckoutFragment} checkout - The checkout record.
 * @param {Currency} currency - The currency.
 * @param {string} countryCode - The country code.
 * @param {string} stateCode - The state code.
 * @param {string} storeSlug - The slug of the store.
 * @returns {GetDeliveryOptionsInput} - The payload for getting delivery options.
 */
export const getDeliveryOptionsPayload = (
  checkout: CheckoutFragment,
  currency: Currency,
  countryCode: string,
  stateCode: string,
  storeSlug: string
): GetDeliveryOptionsInput => ({
  currency,
  countryCode,
  lineItems: checkout.lineItems
    ? checkout.lineItems.map((item: any) => ({
        buyerVariantId: item.buyerVariantId,
        teespringID: item.teespringID,
        quantity: item.quantity,
        fulfillmentDetails: { productionTechniques: item?.fulfillmentDetails.productionTechniques || [] },
      }))
    : [],
  id: checkout.id,
  stateCode,
  storeSlug,
});
